<template>
  <div class="flex flex-col">
    <div v-if="showSearchTools" class="flex flex-col">
      <h3 class="text-lg font-medium pb-3 lg1024:text-2xl">
        {{ $t(pickpointTitleKey) }}
      </h3>
      <div class="pickpoint-buttons flex gap-2 mb-[26px]">
        <div
          class="pickpoints-template-select"
          :class="{
            'pickpoints-template-select__active': selectedTemplate === 'map',
          }"
          data-qa="step3-template-map-btn"
          @click="switchTemplate('map')"
        >
          <MapIcon :color="selectedTemplate === 'map' ? '#D6483D' : 'black'" />
          <p>{{ $t('map_template') }}</p>
        </div>
        <div
          class="pickpoints-template-select"
          :class="{
            'pickpoints-template-select__active': selectedTemplate === 'list',
          }"
          data-qa="step3-template-list-btn"
          @click="switchTemplate('list')"
        >
          <ListIcon
            :color="selectedTemplate === 'list' ? '#D6483D' : 'black'"
          />
          <p>{{ $t('list_template') }}</p>
        </div>
      </div>
      <div class="flex lg1024:flex-row flex-col mt-1">
        <div
          class="relative w-full flex flex-col lg1024:mb-0 mb-2"
          :class="{ filled: modelValue.length > 0 }"
        >
          <label class="flex" for="position-input__main-form">
            {{ autocompleteLabel }}<span class="required">*</span>
          </label>
          <input
            id="position-input__main-form"
            :value="modelValue"
            type="search"
            class="placeholder-mainColor w-full lg1024:mb-0 input-wrap !pl-9"
            data-qa="step3-pickpoint-search-input"
            @input="onInput"
          />
          <img
            alt="search-icon"
            src="@/assets/img/search-md.svg"
            class="search-icon absolute left-2 pr-4 top-[41px]"
          />
        </div>
      </div>
      <ListCheckbox
        v-if="services.length > 1"
        :list="services"
        :direction="isMobile ? 'column' : 'row'"
        class="mt-4 max-sm:mt-0"
        @callbackCheckboxes="(value) => $emit('filterPickpoints', value)"
      />
    </div>

    <div
      v-else
      class="flex flex-col mt-1"
      :class="{ 'lg1024:mb-12 mb-6': multipleTypes }"
    >
      <Skeletor :height="'40px'" :width="'30%'" class="mt-4" />
      <div class="flex mt-2 justify-between">
        <Skeletor :height="'40px'" :width="'75%'" class="contents" />
        <Skeletor
          :height="'40px'"
          :width="'23.3%'"
          class="xl1920:ml-4 contents"
        />
      </div>
      <div class="mt-2 inline-flex">
        <Skeletor :height="'15px'" :width="'15px'" />
        <Skeletor :height="'15px'" :width="'25%'" class="ml-2" />
        <Skeletor :height="'15px'" :width="'15px'" class="ml-6" />
        <Skeletor :height="'15px'" :width="'25%'" class="ml-2" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import ListCheckbox from '@/components/ListCheckbox.vue'
import MapIcon from '@/components/svg/MapIcon.vue'
import ListIcon from '@/components/svg/ListIcon.vue'

defineProps({
  modelValue: { type: String, default: '' },
  showSearchTools: { type: Boolean, default: false },
  services: { type: Array, default: () => [] },
  multipleTypes: { type: Boolean, default: false },
  autocompleteLabel: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  selectedTemplate: { type: String, default: 'list' },
})

const emit = defineEmits([
  'update:modelValue',
  'searchPickpoints',
  'filterPickpoints',
  'switchPickpointsTemplate',
])

const store = useStore()
const pickpointTitleKey = computed(() =>
  store.state.CUR_LOCATION?.CODE === 'CCI' ||
  store.state.CUR_LOCATION?.ALPHA2 === 'PL'
    ? 'checkout_title_pickpoints_PL'
    : 'choose_pickpoint'
)

function switchTemplate(type) {
  emit('switchPickpointsTemplate', type)
}

function onInput(event) {
  const value = event.target.value
  emit('update:modelValue', value)
  emit('searchPickpoints', value)
}
</script>

<style lang="scss" scoped>
.pickpoints-template-select {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 30px;
  border: 1px solid rgba(50, 74, 93, 0.22);
  color: rgba(9, 22, 32, 0.8);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.224px;
  cursor: pointer;

  p {
    margin: 0 8px;
  }
}
.pickpoints-template-select__active {
  color: #d6483d;
  border-color: #d6483d;
}

#position-input__main-form::-webkit-search-cancel-button {
  /* Remove default */
  -webkit-appearance: none;
  /* Now your own custom styles */
  background-image: url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ACBFBF' %3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z' /%3E%3C/svg%3E%0A");
  height: calc(50px / 2);
  width: calc(50px / 2);
  transform: translateX(-100%);
}

label {
  margin-bottom: 6px;
  color: #75828b;
}
</style>
